/**
 * The night manager's browser-side arithmetic.
 *
 * Pure functions, kept out of the components so they run in the node gate: the status label,
 * the countdown, the goal payload the builder sends, and the DST-aware deadline conversion.
 *
 * The deadline conversion matters most. A person picks a wall-clock time in a named zone; the
 * server stores one absolute instant. If the two disagreed — if the preview said 9pm Melbourne
 * and the enforced stop was an hour off — nobody would find out until a run ended at the wrong
 * time. So both sides call the same `@nightshift/ui-local-time`, and this file only shapes the
 * payload around it.
 */
import { zonedWallTimeToInstant, type LocalTimeResult } from '@nightshift/ui-local-time';
import type { NightManagerOverview, NightManagerRunView } from '@nightshift/ui-types';

export type NightManagerStatusLabel = 'Disabled' | 'Enabled' | 'Needs attention' | 'Stopped';

/**
 * What the top-bar button says.
 *
 * "Needs attention" outranks "Enabled" deliberately: a run that is enabled but cannot find a
 * route, or has a blocker nobody has looked at, is the case a person needs to see at a glance.
 * Saying "Enabled" for it would be technically true and practically useless.
 */
export function statusLabel(run: NightManagerRunView | null): NightManagerStatusLabel {
  if (!run) return 'Disabled';
  if (run.status === 'stopped') return 'Stopped';
  if (run.phase === 'needs-attention' || run.warnings.length > 0) return 'Needs attention';
  return 'Enabled';
}

/** The same judgement across every project, for the All projects scope. */
export function overviewLabel(overview: NightManagerOverview | null): NightManagerStatusLabel {
  if (!overview || overview.runs.length === 0) return 'Disabled';
  if (overview.runs.some((run) => run.needsAttention)) return 'Needs attention';
  if (overview.runs.some((run) => run.status === 'enabled')) return 'Enabled';
  return 'Stopped';
}

/** "3h 20m", "18m", "under a minute". Null when the run has no deadline. */
export function formatRemaining(msRemaining: number | null): string | null {
  if (msRemaining === null) return null;
  if (msRemaining < 60_000) return 'under a minute';
  const minutes = Math.floor(msRemaining / 60_000);
  const hours = Math.floor(minutes / 60);
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes % 60}m`;
}

/** "18 minutes old". Used for capacity readings, where the age is the point. */
export function formatAge(ageMs: number | null): string {
  if (ageMs === null) return 'no reading yet';
  const minutes = Math.round(ageMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} old`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'} old`;
}

export interface GoalDraft {
  /**
   * The deadline this run already has, when editing one.
   *
   * A duration is converted to an absolute instant *once*, at enable. Recomputing it as
   * `now + durationMs` on every save silently extended supervision every time somebody edited
   * an unrelated field: enable at 18:00 for four hours, change the active-ticket limit at
   * 21:00, and the deadline quietly moved from 22:00 to 01:00. The established instant is kept
   * unless the person edits the deadline itself.
   */
  establishedDeadlineAt?: number | null;
  scopeKind: 'selection' | 'backlog';
  selectedTicketIds: number[];
  stopOnSelectionComplete: boolean;
  ticketTarget: string;
  deadlineMode: 'none' | 'duration' | 'deadline';
  durationHours: string;
  deadlineLocal: string;
  timezone: string;
  /** The duration the established deadline was created from, to detect a real change. */
  establishedDurationMs?: number | null;
  capacity: Array<{ machineId: number; provider: string; account: string; window: string; model: string | null; thresholdPercent: string }>;
  maxActiveTickets: string;
  checkIntervalMinutes: string;
  instructions: string;
}

export type GoalPayloadResult =
  | { ok: true; goal: Record<string, unknown> }
  | { ok: false; problems: string[] };

function positiveInt(value: string): number | undefined {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Turn the form into the goal the API takes, or a list of what is wrong.
 *
 * Validated here as well as on the server, and the server's answer is authoritative. The point
 * of validating twice is that a person editing a form gets told immediately, not after a round
 * trip that might also have failed for an unrelated reason.
 */
export function buildGoalPayload(draft: GoalDraft, now: number): GoalPayloadResult {
  const problems: string[] = [];
  // Conditions the person *tried* to set, valid or not. A deadline that was rejected for
  // being in the past must not also produce "choose at least one condition" -- two errors for
  // one mistake reads as a broken form.
  let attemptedConditions = 0;

  const scope =
    draft.scopeKind === 'backlog'
      ? { kind: 'backlog' as const }
      : { kind: 'selection' as const, ticketIds: draft.selectedTicketIds };
  if (draft.scopeKind === 'selection' && draft.selectedTicketIds.length === 0) {
    problems.push('Choose at least one ticket, or manage the project backlog.');
  }

  const stop: Record<string, unknown> = {};
  if (draft.stopOnSelectionComplete && draft.scopeKind === 'selection') {
    stop.selectionComplete = true;
    attemptedConditions += 1;
  }

  if (draft.ticketTarget.trim()) {
    attemptedConditions += 1;
    const target = positiveInt(draft.ticketTarget);
    if (target === undefined) problems.push('The ticket target must be a positive whole number.');
    else stop.ticketTarget = target;
  }

  if (draft.deadlineMode !== 'none') attemptedConditions += 1;
  if (draft.deadlineMode === 'duration') {
    const hours = Number(draft.durationHours.trim());
    if (!Number.isFinite(hours) || hours <= 0) {
      problems.push('The duration must be a positive number of hours.');
    } else {
      // Converted once. An edit that leaves the duration alone keeps the instant that was
      // already established, so unrelated changes cannot extend the run.
      const durationMs = Math.round(hours * 3_600_000);
      const established = draft.establishedDeadlineAt;
      const unchanged = established != null && draft.establishedDurationMs === durationMs;
      stop.deadline = {
        at: unchanged ? established : now + durationMs,
        timezone: draft.timezone,
        source: 'duration',
        durationMs,
      };
    }
  } else if (draft.deadlineMode === 'deadline') {
    if (!draft.deadlineLocal.trim()) {
      problems.push('Choose the date and time to stop at.');
    } else {
      const converted: LocalTimeResult = zonedWallTimeToInstant(draft.deadlineLocal, draft.timezone);
      if (!converted.ok) {
        problems.push(converted.reason);
      } else if (converted.at <= now) {
        problems.push('The deadline must be in the future.');
      } else {
        stop.deadline = { at: converted.at, timezone: draft.timezone, source: 'deadline' };
      }
    }
  }

  if (draft.capacity.length > 0) {
    attemptedConditions += draft.capacity.length;
    const conditions = [];
    for (const entry of draft.capacity) {
      const threshold = Number(entry.thresholdPercent.trim());
      if (!Number.isFinite(threshold) || threshold <= 0 || threshold > 100) {
        problems.push('Each capacity threshold must be a percentage above 0 and at most 100.');
        continue;
      }
      conditions.push({
        machineId: entry.machineId,
        provider: entry.provider,
        account: entry.account,
        window: entry.window,
        model: entry.model,
        thresholdPercent: threshold,
      });
    }
    if (conditions.length > 0) stop.capacity = conditions;
  }

  if (Object.keys(stop).length === 0 && attemptedConditions === 0) {
    problems.push('Choose at least one stopping condition. Stop is always available as well.');
  }

  const maxActive = positiveInt(draft.maxActiveTickets);
  if (maxActive === undefined) problems.push('The active-ticket limit must be a positive whole number.');

  const cadence = positiveInt(draft.checkIntervalMinutes);
  if (cadence === undefined || cadence < 1 || cadence > 120) {
    problems.push('The periodic check must be between 1 and 120 minutes.');
  }

  if (new TextEncoder().encode(draft.instructions).length > 16 * 1024) {
    problems.push('Instructions must be at most 16 KiB.');
  }

  if (problems.length > 0) return { ok: false, problems };
  return {
    ok: true,
    goal: {
      scope,
      stop,
      maxActiveTickets: maxActive,
      checkIntervalMinutes: cadence,
      instructions: draft.instructions.trim(),
    },
  };
}

/** One line per event for the activity view. Never renders hidden reasoning. */
export function describeEvent(event: { kind: string; payload: Record<string, unknown>; actorName: string | null }): string {
  const payload = event.payload;
  const ticket = typeof payload.ticketKey === 'string' ? payload.ticketKey : null;
  switch (event.kind) {
    case 'enabled':
      return `${event.actorName ?? 'Someone'} enabled the night manager`;
    case 'user-message':
      return `${event.actorName ?? 'Someone'}: ${String(payload.body ?? '')}`;
    case 'config-edit':
      return `${event.actorName ?? 'Someone'} changed the goal`;
    case 'route-change':
      return `${event.actorName ?? 'Someone'} changed the execution route`;
    case 'wake':
      return `Woke: ${String(payload.cause ?? 'a change needed attention')}`;
    case 'turn-started':
      return `Started a turn on ${String(payload.machine ?? 'a runner')}`;
    case 'summary':
      return String(payload.summary ?? 'Finished a turn');
    case 'decision':
      return `${String(payload.decision ?? 'Decision')}${ticket ? ` on ${ticket}` : ''}: ${String(payload.reason ?? '')}`;
    case 'action':
      return `${String(payload.operation ?? 'action')}${ticket ? ` on ${ticket}` : ''} — ${String(payload.outcome ?? '')}${payload.reason ? `: ${String(payload.reason)}` : ''}`;
    case 'capacity':
      return `Capacity warning for ${String(payload.label ?? 'an account')}: ${String(payload.unavailable ?? 'no fresh reading')}`;
    case 'warning':
      if (payload.kind === 'periodic-schedule') return `Next check scheduled for ${new Date(Number(payload.nextCheckAt ?? 0)).toLocaleTimeString()}`;
      return `Warning: ${String(payload.kind ?? '')} ${Array.isArray(payload.reasons) ? payload.reasons.join('; ') : ''}`.trim();
    case 'error':
      return `Error: ${String(payload.error ?? payload.message ?? 'something went wrong')}`;
    case 'stopped':
      return `Stopped (${String(payload.reason ?? 'unknown')}) after ${String(payload.completed ?? 0)} completed`;
    case 'post-stop':
      return `After the stop: ${String(payload.summary ?? payload.status ?? 'late output recorded')}`;
    default:
      return event.kind;
  }
}
